/**
 * DISPATCH-BUILDER-01 — create a Pegasus-native dispatch (the "Schedule" button
 * finally does something). Writes a `pegasus_dispatch` row into the Incoming
 * queue (status pending_schedule, source pegasus) — the same model the board /
 * Today / on-call intake already render, so a created job shows up everywhere
 * with no extra plumbing. ESC is never touched (DEC-22/25); Pegasus owns the
 * dispatches it creates. The first big step toward Pegasus being THE program.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type Urgency = "asap" | "soon" | "routine";

export interface NewDispatchInput {
  custno: string | null;
  locno: string | null;
  customerName: string;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  summary: string;
  urgency: Urgency;
  laneKey: string;
  targetWindow?: string | null;
  targetDate?: string | null;
  notes?: string | null;
  serviceTypeId?: string | null;
  serviceTypeName?: string | null;
  diagFee?: number | null;
  diagFeeOverridden?: boolean;
  /** RECORDING→DISPATCH: the call this dispatch was created from — attaches the
   *  recording to the new dispatch (plays in its recording section). */
  sourceCallId?: string | null;
  /** Provenance: 'call' when made from a recorded call, else 'manual'. */
  source?: string;
  /** PO-01: a vendor/PM job or PO number given at booking. */
  poNumber?: string | null;
  /** WHO-TO-REACH (34i87): the person the confirmation text / reminder is
   *  addressed to ("Hi Mary"), and an email kept on the visit. */
  contactName?: string | null;
  contactEmail?: string | null;
}

export function useCreatePegasusDispatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: NewDispatchInput) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      // Geocode from the customer's location so the scheduling copilot
      // (suggestTimes needs lat/lng) works on jobs the office books.
      let lat: number | null = null;
      let lng: number | null = null;
      if (v.custno) {
        const { data: loc } = await supabase
          .from("mirror_location")
          .select("latitude, longitude")
          .eq("custno", v.custno)
          .eq("locno", v.locno ?? "00001")
          .limit(1)
          .maybeSingle();
        const g = loc as { latitude: number | null; longitude: number | null } | null;
        lat = g?.latitude ?? null;
        lng = g?.longitude ?? null;
      }
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .insert({
          status: "pending_schedule",
          lane_key: v.laneKey,
          custno: v.custno,
          locno: v.locno,
          lat,
          lng,
          customer_name: v.customerName,
          address: v.address ?? null,
          city: v.city ?? null,
          phone: v.phone ?? null,
          summary: v.summary.trim(),
          notes: v.notes?.trim() || null,
          urgency: v.urgency,
          target_window: v.targetWindow || null,
          target_date: v.targetDate || null,
          service_type_id: v.serviceTypeId ?? null,
          service_type_name: v.serviceTypeName ?? null,
          diag_fee: v.diagFee ?? null,
          diag_fee_overridden: v.diagFeeOverridden ?? false,
          po_number: v.poNumber?.trim() || null,
          contact_name: v.contactName?.trim() || null,
          contact_email: v.contactEmail?.trim() || null,
          source_call_id: v.sourceCallId ?? null,
          tags: [],
          source: v.source ?? "manual",
          created_by: u?.user?.email ?? null,
          created_at: now,
          updated_at: now,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
}
